import {
  type SavedSection,
  type SavedSectionFields,
  toSavedFields,
  toSectionPresetInput,
  useSectionPresets,
} from "@/lib/hooks/sectionPresets/useSectionPresets";
import { getTemplateLabel } from "@/lib/slides/slideTemplates";
import { Button } from "@ai-teach/ui-web";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";
import { describeCount, describeParent } from "./countRules";

function SectionRow({
  section,
  onUse,
  onRename,
  onDelete,
}: {
  section: SavedSection;
  onUse: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  const rule = section.parent
    ? describeParent(section.parent, section.parent.tagId)
    : section.count && describeCount(section.count);

  return (
    <li className="group flex items-center gap-1 rounded-lg hover:bg-black/[0.04] dark:hover:bg-white/5">
      <button
        type="button"
        onClick={onUse}
        className="min-w-0 flex-1 rounded-lg px-3 py-2 text-left"
      >
        <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">
          {section.name}
        </span>
        <span className="mt-0.5 block truncate text-[11px] text-gray-500 dark:text-gray-400">
          {getTemplateLabel(section.template)} · {rule} · {section.referenceSlide.name}
        </span>
      </button>
      <button
        type="button"
        onClick={onRename}
        aria-label={`Rename ${section.name}`}
        className="rounded-md p-1.5 text-gray-500 opacity-0 transition-opacity hover:text-primary-500 focus:opacity-100 group-hover:opacity-100"
      >
        <HiOutlinePencil className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={onDelete}
        aria-label={`Delete ${section.name}`}
        className="mr-1 rounded-md p-1.5 text-gray-500 opacity-0 transition-opacity hover:text-error-500 focus:opacity-100 group-hover:opacity-100"
      >
        <HiOutlineTrash className="h-4 w-4" />
      </button>
    </li>
  );
}

export function SavedSectionBar({
  current,
  currentName,
  appliedId,
  onAppliedIdChange,
  onUse,
}: {
  /** What the editor holds right now, or null until a template and reference slide are picked. */
  current: SavedSectionFields | null;
  /** The editor's name box, so updating a saved section also stores the name the author typed. */
  currentName: string;
  /** The saved section the editor follows; the editor owns it, since a preset stores it. */
  appliedId: string | null;
  onAppliedIdChange: (id: string | null) => void;
  onUse: (section: SavedSection) => void;
}) {
  const { sections, saveSection, updateSection, deleteSection } = useSectionPresets();
  const [isOpen, setIsOpen] = useState(false);
  const [naming, setNaming] = useState<{ id: string | null; name: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const applied = sections.find((one) => one.id === appliedId) ?? null;

  useEffect(() => {
    if (!isOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (listRef.current && !listRef.current.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [isOpen]);

  const use = (section: SavedSection) => {
    onUse(section);
    setIsOpen(false);
  };

  const confirmName = async () => {
    if (!naming || naming.name.trim() === "") return;
    const name = naming.name.trim();
    const renamed = naming.id ? sections.find((one) => one.id === naming.id) : null;

    setSaving(true);
    try {
      if (renamed) {
        await updateSection(renamed.id, toSectionPresetInput(name, toSavedFields(renamed)));
        toast.success(`Renamed to “${name}”`);
      } else if (current) {
        onAppliedIdChange(await saveSection(toSectionPresetInput(name, current)));
        toast.success(`Section “${name}” saved`);
      }
      setNaming(null);
    } catch {
      toast.error(renamed ? "Could not rename the section" : "Could not save the section");
    } finally {
      setSaving(false);
    }
  };

  const overwrite = async () => {
    if (!applied || !current) return;
    // A blank name box keeps the saved name rather than clearing it.
    const name = currentName.trim() || applied.name;
    setSaving(true);
    try {
      await updateSection(applied.id, toSectionPresetInput(name, current));
      toast.success(`Section “${name}” updated`);
    } catch {
      toast.error("Could not update the section");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (section: SavedSection) => {
    try {
      await deleteSection(section.id);
      if (section.id === appliedId) onAppliedIdChange(null);
      toast.success(`Section “${section.name}” deleted`);
    } catch {
      toast.error("Could not delete the section");
    }
  };

  return (
    <div className="relative flex flex-wrap items-center justify-end gap-2" ref={listRef}>
      <Button variant="ghost" size="sm" onClick={() => setIsOpen(!isOpen)}>
        Saved sections{sections.length > 0 ? ` (${sections.length})` : ""}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled={current === null}
        onClick={() => setNaming({ id: null, name: "" })}
      >
        Save as new…
      </Button>
      {applied && (
        <Button
          variant="ghost"
          size="sm"
          disabled={current === null}
          isLoading={saving}
          onClick={overwrite}
        >
          Update “{applied.name}”
        </Button>
      )}

      {isOpen && sections.length > 0 && (
        <div className="panel absolute right-0 top-full z-[70] mt-2 max-h-64 w-96 overflow-hidden overflow-y-auto rounded-xl p-1 shadow-lg">
          <ul>
            {sections.map((one) => (
              <SectionRow
                key={one.id}
                section={one}
                onUse={() => use(one)}
                onRename={() => setNaming({ id: one.id, name: one.name })}
                onDelete={() => void remove(one)}
              />
            ))}
          </ul>
        </div>
      )}

      {naming && (
        <div className="panel absolute right-0 top-full z-[70] mt-2 w-80 space-y-2 rounded-xl p-3 shadow-lg">
          <label htmlFor="section-preset-name" className="label block">
            {naming.id ? "Rename this section" : "Save this section as"}
          </label>
          <input
            id="section-preset-name"
            value={naming.name}
            onChange={(event) => setNaming({ ...naming, name: event.target.value })}
            placeholder="Cloze sentence — one per word"
            className="w-full rounded-lg border border-gray-200 bg-background px-3 py-2 text-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-700"
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setNaming(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={confirmName}
              disabled={naming.name.trim() === ""}
              isLoading={saving}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
